/**
 * Hızlı Havale panel yardımcıları.
 *
 * Sağlayıcının ayarları `settings` kaydında **değildir** — `provider_configs`
 * satırında tek bir JSON gövdesidir (bkz. backend/services/hizlihavale.py).
 * Bunun panel tarafındaki sonucu şudur: alanlar Ayarlar sayfasının ortak `s`
 * state'inde durmaz, kendi state'indedir; dolayısıyla "bu sekmede değişiklik
 * var mı" hesabı da ortak `changedKeys` ile yapılamaz. Karşılaştırma bu yüzden
 * buradadır ve **iki yer birden** okur: kaydedilecek gövde (`changedFields`)
 * ve Kaydet şeridinin görünürlüğü (`dirtyFields`). Ayrışırlarsa yönetici ya
 * kaydedemediği bir değişiklik görür ya da düğmesi hiç çıkmaz.
 *
 * Adı benzese de `hizlicaparalar` ile ilgisi yoktur: ayrı firma, ayrı
 * sözleşme, ayrı state.
 */

export const HIZLIHAVALE_DEFAULTS = {
  enabled: false,
  api_url: 'https://api.hizli.finance',
  dealer_name: '',
  api_key: '',
  api_secret: '',
  iframe_deposit: false,
  deposit_enabled: true,
  withdraw_enabled: true,
  deposit_min_amount: 100,
  deposit_max_amount: 100000,
  withdraw_min_amount: 100,
  withdraw_max_amount: 100000,
  callback_url: '',
  callback_registered: false,
};

export const CALLBACK_PATH = '/api/hizlihavale/callback';

// Sayı ile metin arasındaki gösterim farkı (panelin `100` ile sunucunun
// `"100.00"`u) değişiklik sayılmamalıdır; boş ve tanımsız da aynı şeydir.
const sameValue = (a, b) => {
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    return Boolean(a) === Boolean(b);
  }
  if (typeof a === 'number' || typeof b === 'number') {
    const left = Number(a ?? 0);
    const right = Number(b ?? 0);
    if (Number.isFinite(left) && Number.isFinite(right)) return left === right;
  }
  return String(a ?? '') === String(b ?? '');
};

/**
 * Sunucuya gönderilecek gövde: **yalnızca değişen alanlar**.
 *
 * Tam gövde yazmak kayıtlı anahtarı silerdi (uç birleştirme yapar ama
 * maskelenmiş sırrı da geri yazardı). Maskesi olduğu gibi duran secret
 * gönderilmez — panele geri dönmeyen bir değeri geri yazmak onu bozar.
 */
export const changedFields = (current, initial, options = {}) => {
  const { paymentBase = '', maskedSecret = '' } = options;
  const values = current || {};
  const saved = initial || {};
  const payload = {};
  Object.keys(HIZLIHAVALE_DEFAULTS).forEach((key) => {
    const next = values[key];
    if (next === undefined) return;
    if (key === 'api_secret' && (!next || next === maskedSecret)) return;
    if (!sameValue(next, saved[key])) payload[key] = next;
  });
  // Callback adresi salt okunurdur ve her zaman sitenin güncel kökünden
  // çizilir; kayıttaki değer geride kalmışsa bu sekme kaydedilirken sessizce
  // düzeltilir (Settings.jsx `CALLBACK_FIELDS` kuralının aynısı).
  if (paymentBase) {
    const url = `${paymentBase}${CALLBACK_PATH}`;
    if (!sameValue(saved.callback_url, url)) payload.callback_url = url;
  }
  return payload;
};

/**
 * Kaydet şeridinin görünürlüğü için değişen alanlar.
 *
 * Callback adresi **sayılmaz**: onu yönetici yazmaz, kaydederken kendiliğinden
 * güncel köke çekilir. Sayılsaydı sekme hiçbir şeye dokunulmadan "değişti"
 * görünür ve Kaydet düğmesi kalıcı olarak açık kalırdı.
 */
export const dirtyFields = (current, initial, options = {}) => (
  Object.keys(changedFields(current, initial, options))
    .filter((key) => key !== 'callback_url')
);

export default { HIZLIHAVALE_DEFAULTS, CALLBACK_PATH, changedFields, dirtyFields };
